import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { ToastrService as ToasterService } from 'ngx-toastr';
import { BuildTripViewType } from '@sd-portal/shared/models';

import { UserConfigurationPartialState } from '.';
import * as userConfigurationActions from './user-configuration.actions';
import { USER_CONFIGURATION_MODULE_KEY } from './user-configuration.declarations';
import { userConfigurationQuery } from './user-configuration.selectors';
import { DEFAULT_USER_CONFIGURATION, UserConfiguration, getProblemDetailsMessage, isProblemDetails } from '../_models';
import { SharedUserConfigurationService } from '../shared-user-configuration.service';

const DEFAULT_SAVE_ERROR_MESSAGE = 'An error occurred while saving your configuration. Please try again.';

// Horizontal view is retired; configurations still holding it fall back to By Leg
const withoutHorizontalBuildTripView = (userConfiguration: UserConfiguration): UserConfiguration =>
  userConfiguration.calendarConfiguration?.buildTripViewTypeId === BuildTripViewType.Horizontal
    ? {
        ...userConfiguration,
        calendarConfiguration: {
          ...userConfiguration.calendarConfiguration,
          buildTripViewTypeId: BuildTripViewType.ByLeg
        }
      }
    : userConfiguration;

// shaped by ExceptionService.catchBadResponse, which rethrows { status, statusText, err } for every HTTP error app-wide
const getSaveErrorMessage = (error: { err?: unknown }): string => {
  const body = error?.err;
  return (isProblemDetails(body) && getProblemDetailsMessage(body)) || DEFAULT_SAVE_ERROR_MESSAGE;
};

@Injectable()
export class UserConfigurationEffects {
  private readonly userConfigurationState$ = this.store$.pipe(
    select(userConfigurationQuery.getUserConfigurationState)
  );

  /** init */
  initFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userConfigurationActions.initFeature),
      switchMapTo(this.sharedUserConfigurationService.getUserConfiguration()),
      map(userConfiguration =>
        userConfigurationActions.featureInitSuccess({
          userConfiguration: withoutHorizontalBuildTripView(
            userConfiguration || DEFAULT_USER_CONFIGURATION
          )
        })
      ),
      catchError(() => of(userConfigurationActions.featureInitError()))
    )
  );

  /** save */
  saveUserConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userConfigurationActions.saveUserConfiguration),
      withLatestFrom(this.userConfigurationState$),
      switchMap(([_, state]) =>
        this.sharedUserConfigurationService.saveUserConfiguration(state).pipe(
          map(_ => userConfigurationActions.saveUserConfigurationSuccess()),
          catchError((error: { err?: unknown }) => [
            userConfigurationActions.saveUserConfigurationError({ message: getSaveErrorMessage(error) }),
            userConfigurationActions.initFeature()
          ])
        )
      )
    )
  );

  saveUserConfigurationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userConfigurationActions.saveUserConfigurationError),
        tap(payload => {
          this.toasterService.error('Save Error', payload.message);
        })
      ),
    { dispatch: false }
  );

  saveUserConfigurationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userConfigurationActions.saveUserConfigurationSuccess),
        tap(() => {
          this.toasterService.success('User Configuration Saved');
        })
      ),
    { dispatch: false }
  );

  /** toggle */
  toggleUseFlattenedCalendar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userConfigurationActions.toggleUseFlattenedCalendar),
        withLatestFrom(this.userConfigurationState$),
        switchMap(([action, state]) => {
          const updatedConfiguration: UserConfiguration = {
            ...state,
            calendarConfiguration: {
              ...state.calendarConfiguration,
              useFlattenedCalendar: action.useFlattenedCalendar
            }
          };
          return [
            userConfigurationActions.updateCalendarConfiguration({
              userConfiguration: updatedConfiguration
            }),
            userConfigurationActions.clearState()
          ];
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly sharedUserConfigurationService: SharedUserConfigurationService,
    private readonly store$: Store<{
      [USER_CONFIGURATION_MODULE_KEY]: UserConfigurationPartialState;
    }>,
    private readonly toasterService: ToasterService
  ) {}
}
